import React from 'react';
import './ShareDialogMock.css';

// A drawn, simplified share dialog for the "Share your prototype with everyone" guide. It stands
// for the sharing screens of the design tool, in plain HTML and CSS: no screenshots, no logos.
// Used by both brands (ViewportEditor/ShareGuideModal and Aero/AeroShareGuideModal).
//
//   scene     'a'  the first screen, still private: click the access row
//             'b'  share settings: Anyone, View, Save
//             'c'  the first screen again, now shared: Copy link
//   fileName  the user's own file name (from their link) for the title; "Example project" if none
//   ownerName the user's own name for the "you" row; "You" if none
//
// It also draws its own cursor and the little state changes (the row switching to Anyone, View
// ticking on, Save lighting up, "Link copied"), on the same 17 second clock as the guide
// (`--sg-t`, set by each guide). The outline positions in each guide's CSS are percentages of this
// box, so keep the `top`/`height` values in ShareDialogMock.css in step with them. It is not a
// recording of Figma and never shows a person, only example names.
const Cursor = ({ scene }) => (
  <span className={`sdm-cursor sdm-cursor-${scene}`}>
    <i className="sdm-ripple" />
    <svg width="16" height="20" viewBox="0 0 16 20" fill="none">
      <path d="M1.5 1.2v14.1l3.6-3.4 2.5 5.6 2.5-1.1-2.5-5.5h5.1L1.5 1.2z" fill="#1F2328" stroke="#fff" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  </span>
);

export default function ShareDialogMock({ scene = 'a', fileName, ownerName }) {
  const owner = ownerName || 'You';
  const initial = (owner.trim()[0] || 'Y').toUpperCase();
  const title = fileName || 'Example project';
  if (scene === 'b') {
    return (
      <div className="sdm sdm-settings" aria-hidden="true">
        <div className="sdm-bar"><span className="sdm-back">&lsaquo;</span><b>Share settings</b><span className="sdm-x">&times;</span></div>
        <div className="sdm-label sdm-s-who">Who can access</div>
        <div className="sdm-select sdm-s-select">
          <span className="sdm-swap sdm-swap-old"><i className="sdm-lock" /><b>Only people you invite</b></span>
          <span className="sdm-swap sdm-swap-new"><span className="sdm-globe" /><b>Anyone</b></span>
          <span className="sdm-caret">&#8964;</span>
        </div>
        <div className="sdm-hint sdm-s-hint">
          <span className="sdm-swap sdm-swap-old">Only people you add can open this file.</span>
          <span className="sdm-swap sdm-swap-new">Anyone with the link can open this file.</span>
        </div>
        <div className="sdm-label sdm-muted sdm-s-what">What can they do</div>
        <div className="sdm-radio sdm-s-view"><i className="sdm-dot"><i className="sdm-dot-on" /></i><span>View</span></div>
        <div className="sdm-radio sdm-s-edit"><i className="sdm-dot" /><span>Edit</span></div>
        <div className="sdm-rule sdm-s-rule1" />
        <div className="sdm-label sdm-s-sec">Additional security</div>
        <div className="sdm-radio sdm-s-pw"><i className="sdm-box" /><span>Password required</span></div>
        <div className="sdm-rule sdm-s-rule2" />
        <div className="sdm-label sdm-s-adv">Advanced</div>
        <div className="sdm-radio sdm-s-copy"><i className="sdm-box on" /><span>Viewers can copy and export</span></div>
        <div className="sdm-foot">
          <span className="sdm-btn">Cancel</span>
          <span className="sdm-btn sdm-btn-save"><span className="sdm-btn-fill" /><span className="sdm-btn-text">Save</span></span>
        </div>
        <Cursor scene="b" />
      </div>
    );
  }
  const shared = scene === 'c';
  return (
    <div className={`sdm sdm-dialog ${shared ? 'sdm-shared' : ''}`} aria-hidden="true">
      <div className="sdm-bar sdm-d-bar">
        <b>Share &ldquo;{title}&rdquo;</b>
        <span className="sdm-copy">
          {shared ? (
            <>
              <span className="sdm-swap sdm-swap-old">Copy link</span>
              <span className="sdm-swap sdm-swap-new">Link copied</span>
            </>
          ) : 'Copy link'}
        </span>
        <span className="sdm-x">&times;</span>
      </div>
      <div className="sdm-banner sdm-d-banner">This draft is only visible to you. Move it to share it.</div>
      <div className="sdm-input sdm-d-input">Add emails to invite</div>
      <div className="sdm-label sdm-d-who">Who has access</div>
      <div className="sdm-row sdm-d-anyone">
        {shared ? <span className="sdm-globe" /> : <i className="sdm-lock" />}
        <span>{shared ? 'Anyone' : 'Only people you invite'}</span>
        <em>{shared ? 'can view ›' : '›'}</em>
      </div>
      <div className="sdm-row sdm-d-owner"><span className="sdm-avatar">{initial}</span><span>{owner} <small>(you)</small></span><em>owner</em></div>
      <Cursor scene={scene} />
    </div>
  );
}
